import React, {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery} from 'react-query';
import dynamicAPI from '../../api/dynamicAPI';
import {Helmet} from 'react-helmet-async';
import {Button} from '@mui/material';
import {useTranslation} from 'react-i18next';
import moment from "moment";
import SuggestSpaceDetails from "../../components/view/spaces/SuggestSpaceDetails";
import SpaceViewDetails from "../../components/view/spaces/SpaceViewDetails";
import "../../css/space-view.css";

function SpaceView() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const url = '/api/view/spaces';

    const [previewImage, setPreviewImage] = useState(null);

    const handleImageClick = (imagePath) => setPreviewImage(imagePath);
    const handleCloseImageModal = () => setPreviewImage(null);

    const {data: spaceData, error: spaceError} = useQuery(
        ['Space', id],
        async () => await dynamicAPI('get', `${url}/${id}`, {}),
        {retry: 0, enabled: !!id}
    );

    if (!id) return <div>Invalid Space ID.</div>;
    if (spaceError) return <ErrorFallback error={spaceError} goBack={() => navigate(-1)} />;
    if (!spaceData) return <div>Loading...</div>;

    const space = spaceData.space;
    if (!space) return <div>No space found.</div>;

    return (
        <>
            <Helmet>
                <title>{space.name} | Communitism</title>
                <meta name="description" content={space.description || "Space details"} key="desc" />
                <meta property="og:title" content="Find Cultural Buildings" key="ogtitle" />
                <meta property="og:description" content="Browse a curated collection of buildings, underground spaces" key="ogdesc" />
                <meta property="og:url" content="https://heriscope.communitism.space/spaces" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            <div className="space-view-page">
                <div className="space-view-back-button" onClick={() => navigate('/spaces')}>
                    ← {t("Back to Spaces")}
                </div>

                {/* Image Gallery */}
                <div className="space-images">
                    {space.images?.length > 0 && (
                        <div className="space-image-gallery">
                            {space.images.map((img, index) => (
                                <div
                                    key={index}
                                    className="zoom-hover-building-container"
                                    onClick={() =>
                                        handleImageClick(`${spaceData.images_path}${img?.path || 'item-no-image.png'}`)
                                    }
                                    style={{cursor: 'pointer'}}
                                >
                                    <img src={`${spaceData.images_path}${img.path}`} alt={`Space ${index}`} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-header">
                    <div className="space-header-text text-left">
                        {t("Updated")}: {moment(space.createdAt).format('lll')}
                    </div>
                    <div className="space-header-text text-middle">
                        {t("Building Type")}: {Array.isArray(space.building_type) ? space.building_type.map(t).join(', ') : t("Unknown")}
                    </div>
                </div>

                {/* Main Detail Component */}
                <SpaceViewDetails space={space} t={t} />

                {/* Map + Address */}
                <div className="space-view-right-side right-side-content">
                    <iframe
                        title="Google Map"
                        width="100%"
                        height="200"
                        src={`https://www.google.com/maps?q=${space.lat},${space.lng}&hl=en&z=14&output=embed`}
                        allowFullScreen
                    ></iframe>
                    <div className="space-data-block space-location">
                        <div>{space.address}</div>
                    </div>
                </div>
            </div>

            <SuggestSpaceDetails t={t} spaceId={id} />

            {previewImage && (
                <div className="image-modal-overlay" onClick={handleCloseImageModal}>
                    <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                        <img src={previewImage} alt="Preview" />
                        <button className="close-button" onClick={handleCloseImageModal}>×</button>
                    </div>
                </div>
            )}
        </>
    );
}

function ErrorFallback({error, goBack}) {
    const errorMessage = error?.message || "Unknown error";
    return (
        <div className="user-error-message-block">
            <p>Something went wrong 😭</p>
            <span>Error: {errorMessage}</span>
            <Button onClick={goBack}>Go Back</Button>
        </div>
    );
}

export default SpaceView;
